import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H6Text} from '@buncha/components/core/text';
import {DimensionValue, StyleSheet} from 'react-native';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  timerCounter,
  timerStringInDigits,
} from '@buncha/itemDetails/components/ItemDetail/Counter/counter';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {AOOSShopperStatus} from '@buncha/batchDetails/types';
import AOOSStatusSheet from '@buncha/itemDetails/components/AOOSStatusSheet';
import {Modules} from '@buncha/utils/types';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Touchable} from '@buncha/components/core/touchable';
import moment from 'moment';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  status: AOOSShopperStatus | undefined;
  foundAt: Date | undefined;
  shopper2: string | undefined;
}

const AdjOOSStatus = (props: Props) => {
  const {status, shopper2} = props;
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const [open, setOpen] = useState(false);
  const {body} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};

  const toggleSheet = useCallback(() => setOpen(s => !s), []);

  const date = moment(props?.foundAt);
  const initDaysToExpire = date.diff(moment());
  const initEventDate = moment.duration(initDaysToExpire);

  const AOOSShopperText = useMemo(
    () => ({
      [AOOSShopperStatus.customerApproved]: body?.customerApproved,
      [AOOSShopperStatus.itemFound]: body?.itemFound,
      [AOOSShopperStatus.itemPicked]: body?.itemPicked,
      [AOOSShopperStatus.itemShared]: body?.itemShared,
    }),
    [
      body?.customerApproved,
      body?.itemFound,
      body?.itemPicked,
      body?.itemShared,
    ],
  );

  const AOOSShopperFillPercent: {[key: string]: DimensionValue} = useMemo(
    () => ({
      [AOOSShopperStatus.itemShared]: '25%',
      [AOOSShopperStatus.itemFound]: '50%',
      [AOOSShopperStatus.customerApproved]: '75%',
      [AOOSShopperStatus.itemPicked]: '100%',
    }),
    [],
  );

  const [timer, setTimer] = useState({
    hours: initEventDate.hours(),
    mins: initEventDate.minutes(),
    secs: initEventDate.seconds(),
  });

  const timerFunc = useCallback(() => {
    const {hours, mins, secs} = timerCounter(date);
    setTimer({
      hours,
      mins,
      secs,
    });
  }, [date]);

  const counter = timerStringInDigits(timer);

  useEffect(() => {
    if (date && initDaysToExpire > 0) {
      const startTimer = setInterval(() => {
        timerFunc();
      }, 1000);
      return () => {
        clearInterval(startTimer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  if (!status) return <Box />;

  return (
    <Fragment>
      <Box style={styles.mainContainer}>
        <HBox>
          <H6Text
            style={[
              styles.textAlignLeft,
              {
                fontFamily: muliFont[600].normal,
                color: theme?.text?.blue[300],
              },
            ]}>
            {AOOSShopperText[status]?.replace('{shopper2}', shopper2 ?? '')}
          </H6Text>
          <Touchable style={styles.transparentBox} onPress={toggleSheet}>
            <CustomText
              style={[
                styles.moreDetails,
                {
                  fontFamily: muliFont[700].normal,
                  color: theme?.text?.blue[300],
                },
              ]}>
              {body?.moreDetails}
            </CustomText>
          </Touchable>
        </HBox>
        <Box
          style={[
            styles.outerBox,
            {
              borderColor: theme?.icon?.blue?.[100],
            },
          ]}>
          <Box
            style={[
              styles.innerBox,
              {
                width: AOOSShopperFillPercent[status] || '0%',
                backgroundColor: theme?.icon?.blue?.[100],
              },
            ]}
          />
        </Box>
      </Box>
      <AOOSStatusSheet
        timer={counter}
        open={open}
        onClose={toggleSheet}
        status={status}
      />
    </Fragment>
  );
};

export default React.memo(AdjOOSStatus);

const styles = StyleSheet.create({
  textAlignLeft: {
    textAlign: 'left',
  },
  outerBox: {
    backgroundColor: 'transparent',
    width: '100%',
    borderRadius: 50,
    borderWidth: 1,
    height: 10,
  },
  mainContainer: {
    backgroundColor: 'transparent',
    gap: 5,
  },
  innerBox: {
    height: 8,
    borderRadius: 50,
  },
  moreDetails: {
    textDecorationLine: 'underline',
  },
  transparentBox: {
    backgroundColor: 'transparent',
  },
});
